import { Formik, FormikHelpers } from "formik";
import React, { useEffect, useRef, useState } from "react";
import UiModal from "../../common/ui/UiModal";
import {
  BankAccount,
  HomeAddress,
  PaymentMethod,
  PAYMENT_TYPES,
} from "../models/user-interface";
import * as Yup from "yup";
import { ERRORS } from "../../constants/user-info.const";
import { AccountBalanceOutlined, RoomOutlined } from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  createBankAccount,
  updatePaymentMethod,
} from "../../../services/employeeService";
import { formSubmitErrorHandler } from "../../../services/formService";
import Loader from "../../common/Loader";
import PaymentMethodForm from "../user-forms/PaymentMethodForm";

interface ModalProps {
  open: boolean;
  handleClose: (result?: any, action?: any) => void;
  paymentDetails: PaymentMethod | undefined;
  PaymentMethodUpdateCallback: (result?: any) => void;
  homeAddress: HomeAddress | undefined;
  bankAccount: BankAccount | undefined;
}
interface PaymentData {
  version: string;
  type: string;
  splits?: [];
}
export default function PaymentMethodEditModal(props: ModalProps) {
  const {
    open,
    handleClose,
    PaymentMethodUpdateCallback,
    homeAddress,
    paymentDetails,
    bankAccount,
  } = props;
  const formRef: any = useRef();
  const [isShowFooterLoader, setIsShowFooterLoader] = useState<boolean>(false);

  const title = "Edit Payment Method";
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const mountedRef = useRef(true);

  const validationSchema = Yup.object({
    payment_method: Yup.string().required(ERRORS.paymentMethod),
    name: Yup.string().when("payment_method", {
      is: (value) => value && value === PAYMENT_TYPES.directDeposit && !bankAccount,
      then: Yup.string().required(ERRORS.bankName),
    }),
    routing_number: Yup.string().when("payment_method", {
      is: (value) => value && value === PAYMENT_TYPES.directDeposit && !bankAccount,
      then: Yup.string().required(ERRORS.routingNumber),
    }),
    account_number: Yup.string().when("payment_method", {
      is: (value) => value && value === PAYMENT_TYPES.directDeposit && !bankAccount,
      then: Yup.string().required(ERRORS.accountNumber),
    }),
    account_type: Yup.string().when("payment_method", {
      is: (value) => value && value === PAYMENT_TYPES.directDeposit && !bankAccount,
      then: Yup.string().required(ERRORS.accountType),
    }),
  });

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handlePaymentUpdateData = () => {
    const paymentUpdated = true;
    PaymentMethodUpdateCallback(paymentUpdated);
  };
  const submitBankAccountDetails = (
    { name, routing_number, account_number, account_type }: any,
    formHelpers: FormikHelpers<any>
  ) => {
    const data = {
      name,
      routing_number,
      account_number,
      account_type,
    };

    setIsShowFooterLoader(true);
    formSubmitErrorHandler(
      createBankAccount(data).then((response: any) => {
        setIsShowFooterLoader(false);
        handlePaymentUpdateData();
      }),
      () => {
        setIsShowFooterLoader(false);
      },
      formHelpers.setFieldError
    );
  };

  const updatePaymentMethodInfo = (
    data: PaymentData,
    formHelpers: FormikHelpers<any>
  ) => {
    setIsShowFooterLoader(true);
    formSubmitErrorHandler(
      updatePaymentMethod(data).then((response: any) => {
        setIsShowFooterLoader(false);
        formHelpers.resetForm();
        handlePaymentUpdateData();
      }),
      () => {
        setIsShowFooterLoader(false);
      },
      formHelpers.setFieldError
    );
  };
  const handleSubmit = (values: any, formHelpers: FormikHelpers<any>) => {
    // if payment type is unchanged do nothing
    if (paymentDetails?.type === values.payment_method) {
      PaymentMethodUpdateCallback();
      return;
    }
    if (values.payment_method === PAYMENT_TYPES.directDeposit) {
      submitBankAccountDetails(values, formHelpers);
      return;
    }
    const data = {
      version: paymentDetails?.version,
      type: values.payment_method,
    };
    updatePaymentMethodInfo(data as PaymentData, formHelpers);
  };
  return (
    <UiModal
      open={open}
      handleClose={() => {
        handleClose();
      }}
      title={title}
      handleSave={() => {
        formRef.current?.handleSubmit();
      }}
      showFooterLoader={isShowFooterLoader}
    >
      <>
        <Formik
          initialValues={{
            payment_method: paymentDetails?.type,
            name: "",
            routing_number: "",
            account_number: "",
            account_type: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          innerRef={formRef}
          enableReinitialize
        >
          {(formik, meta, form) => {
            return (
              <div>
                {isLoading ? (
                  <Loader />
                ) : (
                  <FormControl
                    fullWidth
                    error={
                      (meta?.touched || form?.submitCount > 0) &&
                      (meta?.error ? true : false)
                    }
                  >
                    <RadioGroup
                      aria-labelledby="controlled-radio-buttons-group"
                      name="payment_method"
                      value={formik.values.payment_method}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel
                        value={PAYMENT_TYPES.directDeposit}
                        control={<Radio />}
                        label={PAYMENT_TYPES.directDeposit}
                      />

                      {formik?.values?.payment_method ===
                        PAYMENT_TYPES.directDeposit && (
                        <Box ml={5}>
                          <Box mb={2}>
                            <Typography variant="body2">
                              Your paycheck will be deposited to the provided
                              bank account:
                            </Typography>
                          </Box>
                          {!bankAccount && <PaymentMethodForm />}
                          {bankAccount && (
                            <Box display="flex" mb={2}>
                              <Box display="flex">
                                <Box mr={3}>
                                  <Box>
                                    <AccountBalanceOutlined />
                                  </Box>
                                </Box>
                                <div className="payment-method--details-section">
                                  <Typography variant="body2">
                                    {bankAccount?.name}
                                  </Typography>
                                  <Typography variant="body2">
                                    Account: {bankAccount?.account_number}
                                  </Typography>
                                  <Typography variant="body2">
                                    Routing: {bankAccount?.routing_number}
                                  </Typography>
                                  <Typography variant="body2">
                                    {bankAccount?.account_type}
                                  </Typography>
                                </div>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}

                      <FormControlLabel
                        value={PAYMENT_TYPES.check}
                        control={<Radio />}
                        label={PAYMENT_TYPES.check}
                      />
                      {formik?.values?.payment_method === PAYMENT_TYPES.check && (
                        <Box ml={5}>
                          <Box my={2}>
                            <Typography>
                              A check will be given to you by your employer.
                              1-800Accountant does not mail checks.
                            </Typography>
                          </Box>
                          <Box>
                            <Box mb={2}>
                              <Box mb={2}>
                                <Typography variant="body2">
                                  Mailing Address (Required)
                                </Typography>
                              </Box>
                            </Box>
                            <Box mt={2} display="flex">
                              <Box>
                                <RoomOutlined />
                              </Box>

                              {homeAddress?.street_1 && (
                                <div className="payment-method--details-section">
                                  <Typography variant="body2">
                                    <span>{homeAddress?.street_1}</span>
                                    {homeAddress?.street_2 && (
                                      <span>
                                        <span>{homeAddress?.street_2}</span>
                                        {", "}
                                      </span>
                                    )}
                                    <span>{homeAddress?.city},</span>{" "}
                                    <span>{homeAddress?.state || ""}</span>{" "}
                                    <span>{homeAddress?.zip}</span>
                                  </Typography>
                                </div>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </RadioGroup>

                    <FormHelperText>
                      {meta?.touched || form?.submitCount > 0
                        ? meta?.error
                        : ""}
                    </FormHelperText>
                  </FormControl>
                )}
              </div>
            );
          }}
        </Formik>
      </>
    </UiModal>
  );
}
